import { SelectionCard } from '@jouzen/ecom-components/v3/SelectionCard';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useGateValue } from '@statsig/react-bindings';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { EventType } from '@/analytics/types';
import upgradeWarrantyBenefits from '@/app/[locale]/product/_components/Modals/BundleModal/upgradeWarrantyBenefits';
import useContentScroll from '@/app/[locale]/product/_hooks/useContentScroll';
import type MembershipSelection from '@/app/[locale]/product/_steps/enums/MembershipSelection';
import WarrantySelection from '@/app/[locale]/product/_steps/enums/WarrantySelection';
import RichText from '@/app/components/RichText';
import useCurrency from '@/app/hooks/useCurrency';
import type { ListProduct } from '@/queries/Products';

import { PDP_CONTROL, PDP_WARRANTY_INTERSTITIAL } from '../_consts/pdpVariants';
import type { Order } from '../_types/order';
import { getPriceAndDiscount } from '../_utils/getPriceAndDiscount';
import CheckboxGroup from './CheckboxGroup';
import ProductSelectCheckbox from './ProductSelectCheckbox';
import WarrantyContent from './WarrantyContent';

interface ExtendedWarrantySelectionsProps {
  readonly onWarrantySelection: (
    value: MembershipSelection | WarrantySelection,
  ) => void;
  readonly order: Order;
  readonly threeYearWarranty: ListProduct | undefined;
  readonly twoYearWarranty: ListProduct | undefined;
  readonly variant: number;
}

const ExtendedWarrantySelections = ({
  onWarrantySelection,
  order,
  threeYearWarranty,
  twoYearWarranty,
  variant,
}: ExtendedWarrantySelectionsProps) => {
  const t = useTranslations();
  const hsaFsaGate = useGateValue('hsafsa_rollout');
  const { currencyCode } = useCurrency();
  const { scrollRef } = useContentScroll();
  const [twoYearOpen, setTwoYearOpen] = useState(false);
  const [threeYearOpen, setThreeYearOpen] = useState(false);
  const warranyBenefits = upgradeWarrantyBenefits(t);
  const selectedWarranty = order.warranty.value as WarrantySelection;

  const showWarranty =
    variant === PDP_CONTROL || variant === PDP_WARRANTY_INTERSTITIAL;

  const handleAccordionClick = (section: 'two-year' | 'three-year') => {
    if (section === 'two-year') {
      setTwoYearOpen(!twoYearOpen);
    } else {
      setThreeYearOpen(!threeYearOpen);
    }
  };

  // analytics tracking for changing warranty selection and radio change
  // this is used to track the selection of 2-year or 3-year warranty
  const handleWarrantySelection = async (
    selection: WarrantySelection,
  ): Promise<void> => {
    if (
      selection === WarrantySelection.TwoYear &&
      selectedWarranty !== WarrantySelection.TwoYear
    ) {
      await window.ouraAnalytics.track(EventType.ModuleClicked, {
        cta: 'upgrade to 2-year',
        location: 'pdp',
        step: 4,
      });
      onWarrantySelection(WarrantySelection.TwoYear);
    } else if (
      selection === WarrantySelection.ThreeYear &&
      selectedWarranty !== WarrantySelection.ThreeYear
    ) {
      await window.ouraAnalytics.track(EventType.ModuleClicked, {
        cta: 'upgrade to 3-year',
        location: 'pdp',
        step: 4,
      });
      onWarrantySelection(WarrantySelection.ThreeYear);
    } else {
      await window.ouraAnalytics.track(EventType.ModuleClicked, {
        cta: '1-year protection plan',
        location: 'pdp',
        step: 4,
      });
      onWarrantySelection(WarrantySelection.OneYear);
    }
  };

  const { price: warrantyTwoYearPrice } = getPriceAndDiscount(twoYearWarranty);
  const { price: warrantyThreeYearPrice } =
    getPriceAndDiscount(threeYearWarranty);

  return (
    <div
      className="row-start-4 pt-6 md:row-start-2 md:pt-0"
      data-cy="warranty-section"
    >
      {showWarranty ? (
        <>
          <div ref={twoYearOpen ? scrollRef : null}>
            <SelectionCard
              AccordionContent={warranyBenefits}
              accordionOnClick={() => handleAccordionClick('two-year')}
              accordionOpen={twoYearOpen}
              cardSelected={selectedWarranty == WarrantySelection.TwoYear}
              currencyCode={currencyCode}
              data-cy="bundle-warranty-two-year"
              onCheckBoxClick={() =>
                handleWarrantySelection(WarrantySelection.TwoYear)
              }
              price={warrantyTwoYearPrice ?? 45}
              title={t('2_year_protection_plan')}
            />
          </div>
          <div ref={threeYearOpen ? scrollRef : null}>
            <SelectionCard
              AccordionContent={warranyBenefits}
              accordionOnClick={() => handleAccordionClick('three-year')}
              accordionOpen={threeYearOpen}
              cardSelected={selectedWarranty == WarrantySelection.ThreeYear}
              currencyCode={currencyCode}
              data-cy="bundle-warranty-three-year"
              onCheckBoxClick={() =>
                handleWarrantySelection(WarrantySelection.ThreeYear)
              }
              price={warrantyThreeYearPrice ?? 60}
              title={t('3_year_protection_plan')}
            />
          </div>
        </>
      ) : (
        <CheckboxGroup
          label={t('pdp_step_warranty_next')}
          onChange={(value: string[]) => {
            if (!value.length) {
              void handleWarrantySelection(WarrantySelection.OneYear);
            } else {
              void handleWarrantySelection(value.at(-1) as WarrantySelection);
            }
          }}
          value={[selectedWarranty]}
        >
          <div className="flex flex-col gap-y-4">
            <ProductSelectCheckbox
              aria-label={t('2_year_protection_plan')}
              value={WarrantySelection.TwoYear}
            >
              {(state) => (
                <WarrantyContent
                  currencyCode={currencyCode}
                  id={WarrantySelection.TwoYear}
                  price={warrantyTwoYearPrice ?? 45}
                  title="2_year_protection_plan"
                  {...state}
                />
              )}
            </ProductSelectCheckbox>
            <ProductSelectCheckbox
              aria-label={t('3_year_protection_plan')}
              value={WarrantySelection.ThreeYear}
            >
              {(state) => (
                <WarrantyContent
                  currencyCode={currencyCode}
                  id={WarrantySelection.ThreeYear}
                  price={warrantyThreeYearPrice ?? 60}
                  title="3_year_protection_plan"
                  {...state}
                />
              )}
            </ProductSelectCheckbox>
          </div>
        </CheckboxGroup>
      )}

      {hsaFsaGate && (
        <div>
          <Typography className="italic" color="gray" variant="body-small">
            <RichText messageKey="bundle_modal_warranty_hsa_fsa" />
          </Typography>
        </div>
      )}
    </div>
  );
};

export default ExtendedWarrantySelections;
